// src/services/dataPreloader.js

/**
 * Data Preloader for TimeMate Admin
 * Preloads core data at login for instant page loads
 *
 * Strategy:
 * - Priority 1: Critical for first render (blocking)
 * - Priority 2: Nice to have (background, non-blocking)
 */

import { collection, query, where, getDocs, doc, getDoc, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import cacheService from './cacheService';
import { CACHE_TTL } from './cacheConfig';

// Module-level preload status tracking (singleton, not React state).
// Safe in single-threaded JS. Reset on logout via resetPreloadStatus().
let preloadStatus = {
  started: false,
  completed: false,    // NOTE: Set to true after P1 finishes, even if P2 is still loading
  errors: [],
  loadedKeys: []       // Used by isCoreDataReady() to check P1 completion
};

/**
 * Query users for given company IDs
 * @param {string[]} companyIds - Company IDs to query
 * @returns {Promise<Object[]>} Array of user objects
 */
async function queryUsers(companyIds) {
  if (!companyIds?.length) return [];

  // Firestore 'in' operator supports up to 30 values, but we cap at 10
  // as a safety margin for this preload path (avoids large result sets at login)
  const q = query(
    collection(db, 'users'),
    where('companyId', 'in', companyIds.slice(0, 10))
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Query companies by IDs
 * @param {string[]} companyIds - Company IDs to query
 * @returns {Promise<Object[]>} Array of company objects
 */
async function queryCompanies(companyIds) {
  if (!companyIds?.length) return [];

  const promises = companyIds.map(id => getDoc(doc(db, 'companies', id)));
  const docs = await Promise.all(promises);
  return docs.filter(d => d.exists()).map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Query feature flags from company documents
 * @param {string[]} companyIds - Company IDs to query
 * @returns {Promise<Object>} Merged feature flags object
 */
async function queryFeatures(companyIds) {
  const companies = await queryCompanies(companyIds);
  // Merge feature flags across all companies using OR logic:
  // if ANY company has a feature enabled, it's true in the result.
  // This mirrors the FeatureContext aggregation behavior.
  const features = {};
  companies.forEach(company => {
    if (company.features) {
      Object.keys(company.features).forEach(key => {
        if (company.features[key]) features[key] = true;
      });
    }
  });
  return features;
}

/**
 * Query count of pending absences
 * @param {string[]} companyIds - Company IDs to query
 * @returns {Promise<number>} Count of pending absences
 */
async function queryPendingCount(companyIds) {
  if (!companyIds?.length) return 0;

  const q = query(
    collection(db, 'absences'),
    where('companyId', 'in', companyIds.slice(0, 10)),
    where('status', '==', 'pendent')
  );
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
}

/**
 * Core data configuration - defines what to preload and in which priority tier.
 *
 * Priority 1 (blocking): Awaited before login completes. Without these,
 *   the app can't render anything meaningful (no users, no companies, no features).
 * Priority 2 (background): Fire-and-forget after P1 completes. Used for
 *   badge counts and other non-critical UI elements.
 */
const CORE_DATA = [
  {
    key: 'users',
    priority: 1,
    ttl: CACHE_TTL.USER_PROFILES,
    query: queryUsers
  },
  {
    key: 'companies',
    priority: 1,
    ttl: CACHE_TTL.COMPANIES,
    query: queryCompanies
  },
  {
    key: 'features',
    priority: 1,
    ttl: CACHE_TTL.FEATURE_FLAGS,
    query: queryFeatures
  },
  {
    key: 'pending-count',
    priority: 2,
    ttl: CACHE_TTL.PENDING_APPROVALS,
    query: queryPendingCount
  }
];

/**
 * Preload core data at login
 * @param {string[]} adminCompanyIds - Company IDs the admin manages
 * @returns {Promise<void>}
 */
export async function preloadCoreData(adminCompanyIds) {
  if (!adminCompanyIds?.length) {
    return;
  }

  // Reset status
  preloadStatus = {
    started: true,
    completed: false,
    errors: [],
    loadedKeys: []
  };

  // Load priority 1 items in parallel (critical for first render)
  const priority1 = CORE_DATA.filter(d => d.priority === 1);
  await Promise.all(
    priority1.map(async ({ key, query: queryFn, ttl }) => {
      try {
        const data = await queryFn(adminCompanyIds);
        await cacheService.set(key, data, ttl);
        preloadStatus.loadedKeys.push(key);
      } catch (error) {
        preloadStatus.errors.push({ key, error: error.message });
      }
    })
  );

  // Load priority 2 items in background (nice to have, non-blocking)
  const priority2 = CORE_DATA.filter(d => d.priority === 2);
  priority2.forEach(({ key, query: queryFn, ttl }) => {
    queryFn(adminCompanyIds)
      .then(data => {
        cacheService.set(key, data, ttl);
        preloadStatus.loadedKeys.push(key);
      })
      .catch(error => {
        preloadStatus.errors.push({ key, error: error.message });
      });
  });

  preloadStatus.completed = true;
}

/**
 * Get current preload status
 * @returns {Object} Status object with started, completed, errors, loadedKeys
 */
export function getPreloadStatus() {
  return { ...preloadStatus };
}

/**
 * Check if all priority 1 data is loaded
 * @returns {boolean} True if all critical data is cached
 */
export function isCoreDataReady() {
  const priority1Keys = CORE_DATA
    .filter(d => d.priority === 1)
    .map(d => d.key);
  return priority1Keys.every(key => preloadStatus.loadedKeys.includes(key));
}

/**
 * Reset preload status (call on logout)
 */
export function resetPreloadStatus() {
  preloadStatus = {
    started: false,
    completed: false,
    errors: [],
    loadedKeys: []
  };
}

export default { preloadCoreData, getPreloadStatus, isCoreDataReady, resetPreloadStatus };
